import { type Dictionary } from '@ngrx/entity';

import {
  type CanvasElement,
  type CanvasElementId,
  type SlotId,
} from '@blaze-runtime/engine-domain';

import {
  type CanvasElementDefinition,
  type CanvasElementDefinitionInternal,
} from '../../canvas-elements/element-definition';
import { hasProps } from '../../canvas-elements/element-model/element-props-guards';

import { applyInitialStyleProps } from './style-initials';
import { cleanStylePaths, cleanStylePathsFromPropsOverrides } from './style-path-cleanup';
import { hasStyleableSlots } from './style-slot-target';

const DEFAULT_STYLE_INCLUDE_PATHS = ['style'];

export interface ResetStylePropsData {
  entities: Dictionary<CanvasElement>;
  elementId: CanvasElementId;
  slotId?: SlotId;
  definition: CanvasElementDefinition | CanvasElementDefinitionInternal;
}

export function resetStylePropsMutably(data: ResetStylePropsData): void {
  const { definition, elementId, entities, slotId } = data;

  const element = entities[elementId];
  if (!hasProps(element)) {
    return;
  }

  const target = slotId && hasStyleableSlots(element) ? element.slots[slotId] : element;
  if (!target) {
    return;
  }

  const isSlot = target !== element;
  const resolvedDefinition = {
    ...definition,
    initial: isSlot ? definition.slotInitial : definition.initial,
    styleIncludePaths: definition.styleIncludePaths?.length
      ? definition.styleIncludePaths
      : DEFAULT_STYLE_INCLUDE_PATHS,
    styleExcludePaths: definition.styleExcludePaths ?? [],
  };

  target.props = applyInitialStyleProps(
    cleanStylePaths(target.props ?? {}, resolvedDefinition),
    resolvedDefinition,
  );
  target.overrides = cleanStylePathsFromPropsOverrides(target.overrides, resolvedDefinition);
}
